import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useQuery } from 'react-query';
import { Cpu, Eye, ShieldCheck, Users, Lightbulb } from 'lucide-react';
import { aboutService } from '../../../services/aboutService';

gsap.registerPlugin(ScrollTrigger);

const values = [
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Pushing boundaries in embedded systems and IoT technology.',
  },
  {
    icon: ShieldCheck,
    title: 'Quality',
    description: 'Rigorous testing and precision engineering in every device we ship.',
  },
  {
    icon: Cpu,
    title: 'Integrity',
    description: 'Transparent, ethical practices in every client relationship.',
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'Working closely with partners to build solutions that last.',
  },
];

const About = () => {
  const heroRef = useRef(null);
  const { data: aboutContent } = useQuery('aboutContent', aboutService.get);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.hero-content',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }
      );

      gsap.utils.toArray('.reveal-up').forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 85%',
            },
          }
        );
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen">

      {/* Hero Section - height unified to match Home page's HeroSlider banner (min-h-[480px], py-28 md:py-36) */}
      <section
        ref={heroRef}
        className="relative pt-16 overflow-hidden"
      >
        <img
          src="/assets/images/aboutbanner.png"
          alt="About Srishti Wireless Solution"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gray-900/70" />

        <div className="relative z-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="hero-content py-28 md:py-36 min-h-[480px] flex flex-col items-center justify-center text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {aboutContent?.heroSection?.title || 'About Us'}
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              {aboutContent?.heroSection?.subtitle ||
                'Leading the IoT revolution with innovative embedded solutions that transform industries and improve lives.'}
            </p>
          </div>
        </div>
      </section>

      {/* Vision - text left, image right */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">

            <div className="reveal-up">
              <div className="flex items-center gap-3 mb-6">
                <Eye className="w-10 h-10 text-sky-600 flex-shrink-0" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Our Vision
                </h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                {aboutContent?.vision ||
                  "To become the trusted foundation powering the world's most advanced connected infrastructure by delivering innovative, high-performance IoT hardware solutions that redefine industry standards for quality, precision, reliability, and durability. We strive to enable seamless connectivity, intelligent automation, and sustainable technological advancement, empowering businesses and communities to build smarter, safer, and more efficient ecosystems. Through continuous innovation, uncompromising engineering excellence, and a commitment to customer success, we aim to be the preferred global partner for next-generation IoT hardware solutions."}
              </p>
            </div>

            <div className="reveal-up flex justify-center">
              <img
                src="/assets/images/support.jpg"
                alt="Our Vision"
                className="max-w-full h-auto"
              />
            </div>

          </div>
        </div>
      </section>

      {/* Core Values - image left, text right */}
      <section className="py-24 bg-sky-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">

            <div className="reveal-up order-2 md:order-1 flex justify-center">
              <img
                src="/assets/images/ourvision.png"
                alt="Our Core Values"
                className="max-w-full h-auto"
              />
            </div>

            <div className="reveal-up order-1 md:order-2">
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck className="w-10 h-10 text-sky-600 flex-shrink-0" />
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Our Core Values
                </h2>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                The principles that guide everything we do, from engineering decisions to client relationships.
              </p>

              <div className="space-y-5">
                {(aboutContent?.values || values).map((value, index) => {
                  const Icon = value.icon || Lightbulb;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-gray-900">{value.title}: </span>
                        <span className="text-gray-600">{value.description}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default About;